import { Modal, Form, Input, Row, Col, Upload, Image, message } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { resolveFileUrl } from '../../../utils/helpers';

const resolveLogoSrc = resolveFileUrl;

const LogoUploadItem = ({ file, onPreview, onEdit, onRemove }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ position: 'relative', width: 102, height: 102, border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        src={resolveLogoSrc(file.url || file.thumbUrl)}
        alt="logo"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {hovered && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
        }}>
          <EyeOutlined    style={{ color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onPreview(); }} />
          <EditOutlined   style={{ color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onEdit(); }} />
          <DeleteOutlined style={{ color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onRemove(); }} />
        </div>
      )}
    </div>
  );
};

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const ALLOWED_EXTS  = ['jpg', 'jpeg', 'png'];
const MAX_SIZE_MB   = 5;

const SchoolForm = ({ open, onClose, onSubmit, initialValues }) => {
  const [form]        = Form.useForm();
  const [fileList,    setFileList]    = useState([]);
  const [previewSrc,  setPreviewSrc]  = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef(null);
  const isEdit = !!initialValues;

  const onlyDigits = (maxLength) => (e) => e.target.value.replace(/\D/g, '').slice(0, maxLength);

  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
        setFileList(
          initialValues.logo
            ? [{ uid: '-1', name: 'logo', status: 'done', url: initialValues.logo }]
            : [],
        );
      } else {
        form.resetFields();
        setFileList([]);
      }
    }
  }, [open, initialValues, form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();

    const newFile     = fileList.find((f) => f.originFileObj);
    const logoRemoved = !!initialValues?.logo && fileList.length === 0;

    if (newFile || logoRemoved) {
      const formData = new FormData();
      Object.entries(values).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') { formData.append(key, val); }
      });
      if (newFile)       { formData.append('logo', newFile.originFileObj); }
      else if (logoRemoved) { formData.append('logo', ''); }
      await onSubmit(formData);
    } else {
      await onSubmit(values);
    }
  };

  const validateAndSet = (file) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ALLOWED_TYPES.includes(file.type) || !ALLOWED_EXTS.includes(ext)) {
      message.error('Only JPG, JPEG, and PNG image files are allowed.');
      return false;
    }
    if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
      message.error(`Image must be smaller than ${MAX_SIZE_MB}MB`);
      return false;
    }
    const url = URL.createObjectURL(file);
    setFileList([{ uid: file.uid ?? `upload-${Date.now()}`, name: file.name, status: 'done', originFileObj: file, url }]);
    return true;
  };

  const beforeUpload = (file) => {
    validateAndSet(file);
    return Upload.LIST_IGNORE; // always suppress AntD's own upload + list-add
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (fileList.length === 0) return; // AntD handles drops on its own button when slot is empty
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) { validateAndSet(droppedFile); }
  };

  const handlePreview = async (file) => {
    let src = file.url;
    if (!src && file.originFileObj) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    setPreviewSrc(resolveLogoSrc(src));
    setPreviewOpen(true);
  };

  const textOnlyRule = {
    pattern: /^(?!\s)(?!.*\s{2,})[A-Za-z0-9\s'.,&#-]+(?<!\s)$/,
    message: 'No leading, trailing, or multiple spaces. Special characters like hyphens and apostrophes are allowed',
  };

  const mobileRule = {
    pattern: /^[1-9][0-9]{9}$/,
    message: 'Mobile number must be exactly 10 digits and must not start with 0',
  };

  const landlineRule = {
    pattern: /^0[1-9][0-9]{9}$/,
    message: 'Landline must be 11 digits starting with 0 (e.g. 04412345678)',
  };

  const emailRule = { type: 'email', message: 'Enter a valid email address' };

  return (
    <Modal
      title={isEdit ? 'Edit School' : 'Add New School'}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="name"
              label="School Name"
              rules={[
                { required: true, message: 'School name is required' },
                { max: 200, message: 'School name cannot exceed 200 characters' },
                textOnlyRule,
              ]}
            >
              <Input placeholder="Enter school name" maxLength={200} showCount />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="address"
              label="Address"
              rules={[
                { required: true, message: 'Address is required' },
                { min: 15, message: 'Address must be at least 15 characters' },
                { max: 200, message: 'Address cannot exceed 200 characters' },
              ]}
            >
              <Input.TextArea rows={2} placeholder="Enter address" maxLength={200} showCount />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="mobile"
              label="Mobile Number"
              getValueFromEvent={onlyDigits(10)}
              rules={[mobileRule]}
            >
              <Input
                placeholder="Enter 10-digit mobile number"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="landline"
              label="Landline Number"
              getValueFromEvent={onlyDigits(11)}
              rules={[landlineRule]}
            >
              <Input
                placeholder="e.g. 04412345678"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={11}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[emailRule]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="School Logo" extra="JPG, JPEG or PNG · Max 5 MB ">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files[0];
                  if (f) { validateAndSet(f); }
                  e.target.value = '';
                }}
              />
              <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  beforeUpload={beforeUpload}
                  onRemove={() => setFileList([])}
                  onPreview={handlePreview}
                  maxCount={1}
                  accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
                  itemRender={(_, file, __, { remove }) => (
                    <LogoUploadItem
                      file={file}
                      onPreview={() => handlePreview(file)}
                      onEdit={() => fileInputRef.current?.click()}
                      onRemove={remove}
                    />
                  )}
                >
                  {fileList.length === 0 && (
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 6, fontSize: 13 }}>Upload Logo</div>
                      <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Drag & drop or click</div>
                    </div>
                  )}
                </Upload>
              </div>
              <Image
                style={{ display: 'none' }}
                preview={{
                  visible: previewOpen,
                  src: previewSrc,
                  onVisibleChange: (v) => setPreviewOpen(v),
                }}
              />
            </Form.Item>
          </Col>

        </Row>
      </Form>
    </Modal>
  );
};

export default SchoolForm;
